import type { Room, Track } from 'livekit-client'
import { isSafari } from '../../util/DeviceManager'

/**
 * Applies a background-blur processor to the local camera track on `room`.
 *
 * Returns `true` if a processor was attached, `false` if there was no camera
 * track to attach to.
 *
 * This module is the sole place that statically references
 * `@livekit/track-processors` (and the heavy MediaPipe WASM it pulls in).
 * Callers should reach it via `await import('./MeetingBackgroundBlurApplier')`
 * so the chunk is only fetched when the user actually engages blur.
 *
 * livekit-client is intentionally imported as `import type` only — the
 * firebase-repository chunk must not pull livekit-client into the eager
 * bundle. Track.Source.Camera at runtime is the string 'camera', which we
 * cast to keep type-safety without a runtime reference.
 */
const CAMERA_SOURCE = 'camera' as Track.Source

export async function applyBlurToCameraTrack(
  room: Room,
  blurRadius: number
): Promise<boolean> {
  const cameraTrack =
    room.localParticipant.getTrackPublication(CAMERA_SOURCE)?.track
  if (!cameraTrack) return false

  const { BackgroundProcessor } = await import('@livekit/track-processors')
  const processor = BackgroundProcessor({
    mode: 'background-blur',
    blurRadius,
    // Load the MediaPipe WASM bundle from our own origin instead of the
    // jsdelivr CDN default, which our CSP doesn't allow.
    assetPaths: {
      tasksVisionFileSet: '/assets/mediapipe-tasks-vision-wasm',
    },
  })
  await cameraTrack.setProcessor(processor)
  return true
}

export async function removeBlurFromCameraTrack(room: Room): Promise<void> {
  const cameraTrack =
    room.localParticipant.getTrackPublication(CAMERA_SOURCE)?.track
  if (!cameraTrack) return
  await cameraTrack.stopProcessor()
  if (isSafari()) refreshAttachedElements(cameraTrack)
}

/**
 * Safari quirk: after `stopProcessor` swaps the underlying MediaStreamTrack
 * back to the original, video elements bound to the track via `srcObject`
 * don't pick up the new stream and end up blank. Detaching and re-attaching
 * forces the elements to re-read the current MediaStream.
 */
function refreshAttachedElements(track: {
  attachedElements: HTMLMediaElement[]
  detach: (el: HTMLMediaElement) => HTMLMediaElement
  attach: (el: HTMLMediaElement) => HTMLMediaElement
}) {
  const elements = [...track.attachedElements]
  for (const el of elements) {
    track.detach(el)
    track.attach(el)
  }
}
